import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { getSocket, initSocket, disconnectSocket } from "../lib/socket"
import { useAuthStore } from "../stores/auth-store"

/**
 * Hook to initialize socket connection and handle real-time updates
 * Automatically invalidates relevant queries when events are received
 */
export function useSocketConnection() {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket()
      return
    }

    const socket = initSocket()

    // Matter events
    socket.on("matter:created", () => {
      queryClient.invalidateQueries({ queryKey: ["matters"] })
    })

    socket.on("matter:updated", (matterId: string) => {
      queryClient.invalidateQueries({ queryKey: ["matters"] })
      queryClient.invalidateQueries({ queryKey: ["matters", "detail", matterId] })
    })

    socket.on("matter:deleted", () => {
      queryClient.invalidateQueries({ queryKey: ["matters"] })
    })

    // Matter detail events (uppercase format from server)
    socket.on("MATTERS.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["matters", "detail", data.matter] })
      }
      queryClient.invalidateQueries({ queryKey: ["matters"] })
    })

    socket.on("MATTERS.NUMBERS_UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["matters", "detail", data.matter] })
      }
    })

    // Notes/Feed events
    socket.on("NOTES.CREATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["feed", data.matter] })
        queryClient.invalidateQueries({ queryKey: ["matters", "detail", data.matter] })
      }
    })

    // Document events
    socket.on("DOCUMENTS.CREATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    socket.on("DOCUMENTS.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    socket.on("DOCUMENTS.DELETED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    // Document upload completed (after S3 processing)
    socket.on("DOCUMENTS.UPLOADED", (data: { matter?: string }) => {
      // Invalidate all item documents queries (document may be linked to an item)
      queryClient.invalidateQueries({ queryKey: ["item_documents"] })
      // Also invalidate matter documents
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["documents", "matter", data.matter] })
      }
    })

    // Automation events
    socket.on("AUTOMATIONS.UPDATED", (data: { matter?: string }) => {
      if (data?.matter) {
        queryClient.invalidateQueries({ queryKey: ["automations", "instances", data.matter] })
        queryClient.invalidateQueries({ queryKey: ["automations", "logs", data.matter] })
      }
    })

    // Contact events
    socket.on("contact:created", () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
    })

    socket.on("contact:updated", (contactId: string) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["contact", contactId] })
    })

    // Task events
    socket.on("task:created", () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
    })

    socket.on("task:updated", () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
    })

    socket.on("task:completed", () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] })
    })

    // Notification events
    socket.on("notification", (notification: any) => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
      // Could also trigger a toast notification here
    })

    // Notification count updates (for navbar badge)
    socket.on("NOTIFICATIONS.CHANGED", () => {
      queryClient.invalidateQueries({ queryKey: ["notificationCount"] })
    })

    // Sidebar count refresh events - invalidate when tasks, events, items change
    const sidebarRefreshEvents = [
      "TASKS.FINISHED",
      "TASKS.CREATED",
      "TASKS.UPDATED",
      "TASKS.DELETED",
      "EVENTS.FINISHED",
      "EVENTS.CREATED",
      "EVENTS.UPDATED",
      "EVENTS.DELETED",
      "GENERAL_ITEMS.CREATED",
      "GENERAL_ITEMS.UPDATED",
      "GENERAL_ITEMS.FINISHED",
      "MATTER_DOCUMENT_UPLOADS.UPDATED",
      "TEXT_THREADS.UPDATED",
    ]

    sidebarRefreshEvents.forEach((event) => {
      socket.on(event, () => {
        queryClient.invalidateQueries({ queryKey: ["sidebarCounts"] })
      })
    })

    // Division updates
    socket.on("DIVISIONS.CREATED", () => {
      queryClient.invalidateQueries({ queryKey: ["divisions"] })
    })
    socket.on("DIVISIONS.UPDATED", () => {
      queryClient.invalidateQueries({ queryKey: ["divisions"] })
    })
    socket.on("DIVISIONS.DELETED", () => {
      queryClient.invalidateQueries({ queryKey: ["divisions"] })
    })

    // Lead events
    socket.on("LEADS.CREATED", () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] })
      queryClient.invalidateQueries({ queryKey: ["sidebarCounts"] })
    })
    socket.on("LEADS.CLAIMED", () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] })
      queryClient.invalidateQueries({ queryKey: ["sidebarCounts"] })
    })

    // Text thread events
    socket.on("TEXT_THREADS.UPDATED", (data: { thread?: string }) => {
      // Invalidate thread list
      queryClient.invalidateQueries({ queryKey: ["textThreads", "list"] })
      // If we have a specific thread ID, invalidate that detail
      if (data?.thread) {
        queryClient.invalidateQueries({ queryKey: ["textThreads", "detail", data.thread] })
      }
    })

    // Matter detail events to clean up
    const matterDetailEvents = [
      "MATTERS.UPDATED",
      "MATTERS.NUMBERS_UPDATED",
      "NOTES.CREATED",
      "DOCUMENTS.CREATED",
      "DOCUMENTS.UPDATED",
      "DOCUMENTS.DELETED",
      "DOCUMENTS.UPLOADED",
      "AUTOMATIONS.UPDATED",
    ]

    return () => {
      // Clean up all listeners
      socket.off("matter:created")
      socket.off("matter:updated")
      socket.off("matter:deleted")
      matterDetailEvents.forEach((event) => {
        socket.off(event)
      })
      socket.off("contact:created")
      socket.off("contact:updated")
      socket.off("task:created")
      socket.off("task:updated")
      socket.off("task:completed")
      socket.off("notification")
      socket.off("NOTIFICATIONS.CHANGED")
      sidebarRefreshEvents.forEach((event) => {
        socket.off(event)
      })
      socket.off("DIVISIONS.CREATED")
      socket.off("DIVISIONS.UPDATED")
      socket.off("DIVISIONS.DELETED")
      socket.off("LEADS.CREATED")
      socket.off("LEADS.CLAIMED")
      socket.off("TEXT_THREADS.UPDATED")
      disconnectSocket()
    }
  }, [isAuthenticated, queryClient])
}

/**
 * Hook to get the current socket instance
 */
export function useSocket() {
  return getSocket()
}
